/**
 * Audio Test Component
 * 
 * Simple component to test if audio files are accessible and playable
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Volume2, Play, AlertCircle, CheckCircle } from 'lucide-react';

export function AudioTest() {
  const [testResults, setTestResults] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});

  const audioFiles = [
    { name: 'click1', url: '/audio/typing/click1.mp3' },
    { name: 'click5 (soft)', url: '/audio/typing/click5.mp3' },
    // heartbeat removed from UI but file still exists
  ];

  const testAudioFile = async (name: string, url: string) => {
    setIsLoading(prev => ({ ...prev, [name]: true }));
    
    try {
      // First test if file exists via fetch
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // Test if it can be played
      const audio = new Audio(url);
      audio.volume = 0.3;
      
      await new Promise((resolve, reject) => {
        audio.oncanplaythrough = () => {
          audio.play()
            .then(() => {
              setTestResults(prev => ({ ...prev, [name]: '✅ Success - File loaded and played' }));
              resolve(true);
            })
            .catch(reject);
        };
        
        audio.onerror = () => {
          reject(new Error('Audio element failed to load'));
        };
        
        audio.onloadstart = () => {
          console.log(`Loading ${name}...`);
        };
        
        // Set source to trigger loading
        audio.src = url;
      });
      
    } catch (error) {
      console.error(`Audio test failed for ${name}:`, error);
      setTestResults(prev => ({ 
        ...prev, 
        [name]: `❌ Failed: ${error instanceof Error ? error.message : 'Unknown error'}` 
      }));
    } finally {
      setIsLoading(prev => ({ ...prev, [name]: false }));
    }
  };

  const testAllAudio = async () => {
    setTestResults({});
    for (const file of audioFiles) {
      await testAudioFile(file.name, file.url);
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Volume2 className="h-5 w-5" />
          Audio File Test
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button onClick={testAllAudio} variant="default">
            Test All Audio Files
          </Button>
        </div>

        <div className="space-y-3">
          {audioFiles.map(file => (
            <div key={file.name} className="flex items-center justify-between p-3 border rounded">
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => testAudioFile(file.name, file.url)}
                  disabled={isLoading[file.name]}
                  variant="outline"
                  size="sm"
                >
                  {isLoading[file.name] ? (
                    'Testing...'
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Test
                    </>
                  )}
                </Button>
                
                <div>
                  <div className="font-medium">{file.name}</div>
                  <div className="text-sm text-muted-foreground">{file.url}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {testResults[file.name] && (
                  <Badge variant={testResults[file.name].startsWith('✅') ? 'default' : 'destructive'}>
                    {testResults[file.name].startsWith('✅') ? (
                      <CheckCircle className="mr-1 h-3 w-3" />
                    ) : (
                      <AlertCircle className="mr-1 h-3 w-3" />
                    )}
                    {testResults[file.name].startsWith('✅') ? 'Success' : 'Failed'}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>

        {Object.keys(testResults).length > 0 && (
          <div className="mt-4 p-3 bg-muted rounded">
            <h4 className="font-medium mb-2">Test Results:</h4>
            <div className="space-y-1 text-sm">
              {Object.entries(testResults).map(([name, result]) => (
                <div key={name}>
                  <strong>{name}:</strong> {result}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
